import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import yellow from '@material-ui/core/colors/yellow';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import blue from '@material-ui/core/colors/blue';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import withWidth from '@material-ui/core/withWidth';
import Hidden from '@material-ui/core/Hidden';

import websiteImage from '../../../assets/images/website-3_websites-desktop-april2019.jpg';
import websiteMobileImage from '../../../assets/images/website-3_websites-desktop-mobile-april2019.jpg';
import toJS from '../../../lib/hocs/toJS';
import { heapTrack } from '../../../lib/functions/utils';

const dialogStyles = {
  title: {
    textAlign: 'center',
  },
  contentText: {
    fontFamily: 'Roboto',
    color: '#424242',
    textAlign: 'center',
  },
  // footnote: {
  //   fontSize: 12,
  //   marginTop: 32,
  // },
  primaryButton: {
    color: blue[500],
  },
  closeIcon: {
    position: 'absolute',
    right: 0,
    top: 0,
  },
};

const CloseButton = withStyles(dialogStyles)(({ onClose, classes }) => {
  return (
    <IconButton className={classes.closeIcon} onClick={onClose}>
      <CloseIcon />
    </IconButton>
  );
});

const DialogDesign = withStyles(dialogStyles)(
  ({ onClose, onNext, classes }) => {
    return (
      <React.Fragment>
        <DialogTitle className={classes.title}>
          <div>Active Website with Custom Design</div>
          <CloseButton onClose={onClose} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.contentText}>
            When you subscribe to a plan, we will activate your website to the
            public as well as enhance the design by providing a custom
            background image based on your logos and colors, a banner image
            across the top that says the name of your organization, and
            we&apos;ll clean up your logo as needed.
          </DialogContentText>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Hidden lgUp>
              <img
                src={websiteMobileImage}
                style={{ width: '100%', height: 'auto' }}
              />
            </Hidden>
            <Hidden mdDown>
              <img
                src={websiteImage}
                style={{ width: '100%', height: 'auto' }}
              />
            </Hidden>
          </div>
        </DialogContent>
        <DialogActions>
          <Button className={classes.primaryButton} onClick={onNext}>
            Next
          </Button>
        </DialogActions>
      </React.Fragment>
    );
  }
);

const DialogDomain = withStyles(dialogStyles)(
  ({ onClose, onBack, onNext, classes }) => {
    return (
      <React.Fragment>
        <DialogTitle className={classes.title}>
          <div>Domain Name</div>
          <CloseButton onClose={onClose} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.contentText}>
            <div>
              You can use a custom domain name (like{' '}
              <strong>your-organization.com</strong>) for your website.
              Don&apos;t have one already? No problem! We include one at no
              additional cost when you sign up for the Starter plan or above!
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onBack}>Back</Button>
          <Button className={classes.primaryButton} onClick={onNext}>
            Next
          </Button>
        </DialogActions>
      </React.Fragment>
    );
  }
);

const DialogMessages = withWidth()(
  withStyles(dialogStyles)(({ onClose, onBack, classes, width }) => {
    return (
      <React.Fragment>
        <DialogTitle className={classes.title}>
          <div>Send Messages</div>
          <CloseButton onClose={onClose} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.contentText}>
            You&apos;ll also be able to send important messages via text message
            and email to parents, coaches, and participants! Check out the video
            below to learn more.
          </DialogContentText>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/7oJZzdYvHko?rel=0&amp;showinfo=0"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ maxWidth: '100%', height: 'auto', minHeight: 315 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          {width !== 'xs' && <Button onClick={onBack}>Back</Button>}
          {/* <Button
            href="https://www.jerseywatch.com/schedule-a-call/?utm_source=jw_wa"
            target="_blank"
            className={classes.primaryButton}
          >
            Schedule A Call/Demo
          </Button> */}
          <Button
            className={classes.primaryButton}
            href="/admin/subscription/new"
          >
            View Plans
          </Button>
        </DialogActions>
      </React.Fragment>
    );
  })
);

const styles = theme => ({
  container: {
    textAlign: 'center',
    margin: '16px auto 0',
    width: '100%',
    maxWidth: 550,
    padding: 16,
    opacity: 0.95,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: yellow[100],
    [theme.breakpoints.down('xs')]: {
      padding: 8,
    },
  },
  button: {
    marginLeft: 16,
    [theme.breakpoints.down('xs')]: {
      fontSize: 16,
    },
  },
  title: {
    [theme.breakpoints.down('xs')]: {
      fontSize: 18,
    },
  },
});

const JWSubscriptionWidget = ({
  canEditOrganization,
  classes,
  organization,
  fullScreen,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1);

  if (!canEditOrganization) return null;
  if (organization.force_remove_activate_message) return null;
  if (organization.has_subscription) return null;

  const onClose = () => setDialogOpen(false);

  return (
    <div style={{ padding: '0 16px ' }}>
      <Paper className={classes.container}>
        <Typography variant="h5" className={classes.title}>
          Ready to go Live?
        </Typography>
        <Button
          variant="contained"
          className={classes.button}
          onClick={() => {
            heapTrack('Organizer Clicked To See What They Get On Paid Plan');
            setDialogOpen(true);
          }}
        >
          What You Get When You Subscribe...
        </Button>
        <Dialog
          // fullWidth
          // maxWidth="md"
          fullScreen={fullScreen}
          style={{ zIndex: 1400 }}
          // style={{ maxWidth: 800, margin: 'auto' }}
          open={dialogOpen}
          onClose={onClose}
          onExited={() => setStep(1)}
        >
          {step === 1 && (
            <DialogDesign onClose={onClose} onNext={() => setStep(2)} />
          )}
          {step === 2 && (
            <DialogDomain
              onClose={onClose}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <DialogMessages onClose={onClose} onBack={() => setStep(2)} />
          )}
        </Dialog>
      </Paper>
    </div>
  );
};

JWSubscriptionWidget.propTypes = {
  canEditOrganization: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  organization: PropTypes.object.isRequired,
  fullScreen: PropTypes.bool.isRequired,
};
JWSubscriptionWidget.defaultProps = {};

export default toJS(
  withStyles(styles)(withMobileDialog()(JWSubscriptionWidget))
);
